import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQCategory {
  id: string;
  icon: string;
  title: string;
  color: string;
  items: FAQItem[];
}

const faqCategories: FAQCategory[] = [
  {
    id: 'avcb',
    icon: 'ri-fire-line',
    title: 'AVCB — Auto de Vistoria do Corpo de Bombeiros',
    color: '#D4AF37',
    items: [
      {
        question: 'O que é o AVCB e para que serve?',
        answer:
          'O AVCB (Auto de Vistoria do Corpo de Bombeiros) é o documento emitido pelo Corpo de Bombeiros que atesta que um imóvel atende a todas as exigências de segurança contra incêndio e pânico previstas na legislação estadual. Ele é obrigatório para a maioria dos estabelecimentos comerciais, industriais e de serviços, sendo exigido para alvará de funcionamento, financiamentos e seguros.',
      },
      {
        question: 'Quais estabelecimentos são obrigados a ter AVCB?',
        answer:
          'São obrigados a ter AVCB todos os imóveis com área construída superior a 750 m², edificações com mais de um pavimento, locais de reunião de público (igrejas, restaurantes, academias), indústrias, hospitais, hotéis, escolas e qualquer estabelecimento que armazene ou manipule materiais inflamáveis. A obrigatoriedade varia conforme o Decreto Estadual nº 63.911/2018 em São Paulo.',
      },
      {
        question: 'Qual é a validade do AVCB?',
        answer:
          'A validade do AVCB varia conforme o tipo de ocupação do imóvel, podendo ser de 1 a 5 anos. Após o vencimento, é necessário realizar nova vistoria e renovar o documento. A Engeporto acompanha todo o processo de renovação para garantir que seu estabelecimento esteja sempre regularizado.',
      },
      {
        question: 'Quais documentos são necessários para obter o AVCB?',
        answer:
          'Para obter o AVCB são necessários: projeto técnico de prevenção de incêndio aprovado pelo Corpo de Bombeiros, ART (Anotação de Responsabilidade Técnica) do engenheiro responsável, planta baixa do imóvel, memorial descritivo dos sistemas de proteção instalados, e comprovante de execução das instalações (extintores, hidrantes, iluminação de emergência, saídas de emergência, etc.).',
      },
      {
        question: 'Quanto tempo leva para obter o AVCB?',
        answer:
          'O prazo médio para obtenção do AVCB varia entre 30 e 90 dias, dependendo da complexidade do imóvel, da fila de análise do Corpo de Bombeiros e da necessidade de adequações. Com a Engeporto, o processo é conduzido de forma ágil, com acompanhamento em todas as etapas para evitar pendências e retrabalhos.',
      },
      {
        question: 'O que acontece se minha empresa não tiver AVCB?',
        answer:
          'A ausência do AVCB pode resultar em multas, interdição do estabelecimento pelo Corpo de Bombeiros ou pela Vigilância Sanitária, impossibilidade de renovar o alvará de funcionamento, invalidação de apólices de seguro e responsabilização civil e criminal do proprietário em caso de sinistro.',
      },
    ],
  },
  {
    id: 'clcb',
    icon: 'ri-building-line',
    title: 'CLCB — Certificado de Licença do Corpo de Bombeiros',
    color: '#C0392B',
    items: [
      {
        question: 'Qual a diferença entre AVCB e CLCB?',
        answer:
          'O CLCB (Certificado de Licença do Corpo de Bombeiros) é destinado a edificações de menor porte e risco, geralmente com área inferior a 750 m² e ocupação de baixo risco. Diferente do AVCB, o CLCB é emitido com base em autodeclaração do responsável técnico, sem necessidade de vistoria presencial do Corpo de Bombeiros, tornando o processo mais ágil e simplificado.',
      },
      {
        question: 'Quem pode solicitar o CLCB?',
        answer:
          'O CLCB é destinado a pequenos estabelecimentos comerciais, escritórios, consultórios e imóveis residenciais multifamiliares de baixo risco que se enquadrem nos critérios estabelecidos pelo Decreto Estadual nº 63.911/2018. A classificação correta da ocupação e do risco é fundamental para determinar se o imóvel se enquadra no CLCB ou no AVCB.',
      },
      {
        question: 'O CLCB substitui o AVCB?',
        answer:
          'Sim, para os imóveis que se enquadram nos critérios do CLCB, ele substitui o AVCB como documento de regularização junto ao Corpo de Bombeiros. Porém, é fundamental que a classificação seja feita corretamente por um engenheiro habilitado, pois utilizar o CLCB em imóveis que exigem AVCB pode gerar irregularidades e responsabilização.',
      },
      {
        question: 'Qual a validade do CLCB?',
        answer:
          'O CLCB tem validade de 3 anos para a maioria das ocupações. Após esse período, é necessário realizar a renovação do certificado, verificando se as condições do imóvel e os sistemas de proteção contra incêndio continuam em conformidade com as normas vigentes.',
      },
    ],
  },
  {
    id: 'seguranca',
    icon: 'ri-shield-check-line',
    title: 'Segurança do Trabalho',
    color: '#27AE60',
    items: [
      {
        question: 'O que é o PGR e quem é obrigado a ter?',
        answer:
          'O PGR (Programa de Gerenciamento de Riscos) é um documento obrigatório pela NR-1 para todas as empresas que possuam empregados regidos pela CLT, independentemente do porte ou setor de atividade. Ele substitui o antigo PPRA e tem como objetivo identificar, avaliar e controlar os riscos ocupacionais presentes no ambiente de trabalho, garantindo a saúde e segurança dos trabalhadores.',
      },
      {
        question: 'O que é o LTCAT e quando é necessário?',
        answer:
          'O LTCAT (Laudo Técnico das Condições Ambientais do Trabalho) é um documento exigido pelo INSS para fins previdenciários, especificamente para comprovar a exposição do trabalhador a agentes nocivos que justifiquem a aposentadoria especial. Deve ser elaborado por médico do trabalho ou engenheiro de segurança do trabalho e atualizado sempre que houver mudanças nas condições de trabalho.',
      },
      {
        question: 'Qual a diferença entre insalubridade e periculosidade?',
        answer:
          'Insalubridade refere-se à exposição a agentes físicos, químicos ou biológicos acima dos limites de tolerância estabelecidos pela NR-15, gerando adicional de 10%, 20% ou 40% sobre o salário mínimo. Periculosidade é a exposição a condições de risco acentuado (inflamáveis, explosivos, energia elétrica, radiações ionizantes, etc.), gerando adicional de 30% sobre o salário base, conforme NR-16.',
      },
      {
        question: 'Com que frequência os laudos de segurança do trabalho precisam ser renovados?',
        answer:
          'O PGR deve ser revisado anualmente ou sempre que houver mudanças nas condições de trabalho. O LTCAT deve ser atualizado quando houver alteração nos processos produtivos ou nas condições ambientais. Os laudos de insalubridade e periculosidade devem ser renovados sempre que houver mudanças nas atividades ou no ambiente de trabalho. A Engeporto oferece planos de manutenção periódica para manter toda a documentação em dia.',
      },
      {
        question: 'Quais são as penalidades por não ter a documentação de segurança do trabalho em dia?',
        answer:
          'As penalidades incluem multas administrativas aplicadas pelo Ministério do Trabalho e Emprego (MTE) que variam de R$ 402,53 a R$ 4.025,33 por infração, podendo ser dobradas em caso de reincidência. Além disso, a empresa pode sofrer interdição de setores ou de toda a atividade, ações trabalhistas por parte dos empregados e responsabilização civil e criminal em caso de acidente de trabalho.',
      },
      {
        question: 'A Engeporto atende empresas de todos os portes?',
        answer:
          'Sim! A Engeporto atende desde microempresas e MEIs até grandes indústrias nas regiões metropolitanas de Sorocaba e Campinas. Cada projeto é desenvolvido de forma personalizada, considerando o porte, o setor de atividade e as necessidades específicas de cada cliente. Entre em contato para uma avaliação gratuita.',
      },
    ],
  },
  {
    id: 'geral',
    icon: 'ri-information-line',
    title: 'Dúvidas Gerais',
    color: '#8E44AD',
    items: [
      {
        question: 'A Engeporto atende em quais regiões?',
        answer:
          'A Engeporto atende toda a Região Metropolitana de Sorocaba e a Região Metropolitana de Campinas, incluindo municípios como Sorocaba, Votorantim, Itu, Salto, Indaiatuba, Campinas, Jundiaí, Americana, Piracicaba e cidades vizinhas. Para projetos fora dessas regiões, consulte nossa equipe.',
      },
      {
        question: 'Como solicitar um orçamento?',
        answer:
          'Você pode solicitar um orçamento pelo telefone (15) 99671-9308, pelo e-mail engeporto.contato@gmail.com ou pelo formulário de contato em nosso site. Nossa equipe retorna em até 24 horas úteis com uma proposta personalizada para sua necessidade.',
      },
      {
        question: 'A Engeporto emite ART para os projetos?',
        answer:
          'Sim. Todos os projetos e laudos elaborados pela Engeporto são acompanhados de ART (Anotação de Responsabilidade Técnica) registrada no CREA-SP, garantindo a responsabilidade técnica e a validade legal dos documentos perante órgãos públicos, seguradoras e fiscalizações.',
      },
      {
        question: 'É possível regularizar um imóvel que nunca teve AVCB ou CLCB?',
        answer:
          'Sim, é possível e é exatamente o que a Engeporto faz com frequência. O processo envolve um diagnóstico inicial do imóvel, identificação das adequações necessárias, elaboração do projeto técnico, execução ou orientação das obras de adequação e acompanhamento junto ao Corpo de Bombeiros até a emissão do documento. Entre em contato para uma avaliação.',
      },
    ],
  },
];

const FAQAccordion = () => {
  const [activeCategory, setActiveCategory] = useState<string>('avcb');
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({});

  const toggleItem = (key: string) => {
    setOpenItems((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const activeData = faqCategories.find((c) => c.id === activeCategory)!;

  return (
    <section className="py-12 md:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Category Tabs */}
        <div className="flex flex-wrap gap-2 md:gap-3 mb-8 md:mb-12 justify-center">
          {faqCategories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => {
                setActiveCategory(cat.id);
                setOpenItems({});
              }}
              className={`flex items-center gap-1.5 md:gap-2 px-3 md:px-5 py-2 md:py-3 rounded-full text-xs md:text-sm font-semibold transition-all cursor-pointer whitespace-nowrap border ${
                activeCategory === cat.id
                  ? 'bg-[#0A1628] text-white border-[#0A1628] shadow-lg'
                  : 'bg-white text-neutral-600 border-neutral-200 hover:border-[#0A1628] hover:text-[#0A1628]'
              }`}
            >
              <i className={`${cat.icon} text-sm md:text-base`}></i>
              {cat.title.split('—')[0].trim()}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12">
          {/* Left: Category Info */}
          <motion.div
            key={activeCategory}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
            className="lg:col-span-4"
          >
            <div className="lg:sticky lg:top-32">
              <div
                className="w-12 h-12 md:w-16 md:h-16 flex items-center justify-center rounded-2xl mb-4 md:mb-6"
                style={{ backgroundColor: `${activeData.color}20` }}
              >
                <i
                  className={`${activeData.icon} text-2xl md:text-3xl`}
                  style={{ color: activeData.color }}
                ></i>
              </div>
              <h2 className="font-display font-bold text-xl md:text-2xl text-[#0A1628] mb-3 md:mb-4 leading-snug">
                {activeData.title}
              </h2>
              <p className="text-neutral-500 text-xs md:text-sm leading-relaxed mb-6 md:mb-8">
                {activeData.items.length} perguntas respondidas sobre este tema pela equipe técnica da Engeporto.
              </p>

              <div className="bg-[#0A1628] rounded-2xl p-5 md:p-6 text-white">
                <div className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center bg-[#D4AF37]/20 rounded-xl mb-3 md:mb-4">
                  <i className="ri-customer-service-2-line text-lg md:text-xl text-[#D4AF37]"></i>
                </div>
                <p className="text-xs md:text-sm font-semibold mb-2">Não encontrou sua dúvida?</p>
                <p className="text-[10px] md:text-xs text-neutral-400 leading-relaxed mb-3 md:mb-4">
                  Fale diretamente com nossos engenheiros especializados.
                </p>
                <a
                  href="/contato"
                  className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0A1628] px-3 md:px-4 py-2 md:py-2.5 rounded-md text-[10px] md:text-xs font-bold hover:bg-[#C4A137] transition-all whitespace-nowrap cursor-pointer"
                >
                  FALAR COM ESPECIALISTA
                  <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>
          </motion.div>

          {/* Right: Accordion */}
          <motion.div
            key={`${activeCategory}-items`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="lg:col-span-8 space-y-2 md:space-y-3"
          >
            {activeData.items.map((item, index) => {
              const key = `${activeCategory}-${index}`;
              const isOpen = !!openItems[key];
              return (
                <div
                  key={key}
                  className={`border rounded-xl overflow-hidden transition-all ${
                    isOpen
                      ? 'border-[#D4AF37] shadow-md'
                      : 'border-neutral-200 hover:border-neutral-300'
                  }`}
                >
                  <button
                    onClick={() => toggleItem(key)}
                    className="w-full flex items-center justify-between gap-3 md:gap-4 px-4 md:px-6 py-4 md:py-5 text-left cursor-pointer bg-white hover:bg-neutral-50 transition-colors"
                  >
                    <span className="font-semibold text-[#0A1628] text-xs md:text-sm leading-snug">
                      {item.question}
                    </span>
                    <span
                      className={`w-7 h-7 md:w-8 md:h-8 flex items-center justify-center rounded-full flex-shrink-0 transition-all ${
                        isOpen
                          ? 'bg-[#D4AF37] text-[#0A1628]'
                          : 'bg-neutral-100 text-neutral-500'
                      }`}
                    >
                      <i
                        className={`ri-${isOpen ? 'subtract' : 'add'}-line text-sm md:text-base`}
                      ></i>
                    </span>
                  </button>

                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div className="px-4 md:px-6 pb-4 md:pb-6 pt-0">
                          <div className="h-px bg-neutral-100 mb-3 md:mb-4"></div>
                          <p className="text-neutral-600 text-xs md:text-sm leading-relaxed">
                            {item.answer}
                          </p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default FAQAccordion;
